import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Star, Quote } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
interface Testimonial {
  id: string;
  client_name: string;
  client_position: string;
  client_company: string;
  testimonial_text: string;
  rating: number;
  project_type?: string;
  client_photo_url?: string;
  company_logo_url?: string;
  featured: boolean;
}
const Testimonials = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [filteredTestimonials, setFilteredTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [ratingFilter, setRatingFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  useEffect(() => {
    loadTestimonials();
  }, []);
  useEffect(() => {
    filterTestimonials();
  }, [testimonials, ratingFilter, projectFilter]);
  const loadTestimonials = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from("testimonials").select("*").eq("is_published", true).order("featured", {
        ascending: false
      }).order("display_order", {
        ascending: true
      }).order("created_at", {
        ascending: false
      });
      if (error) throw error;
      setTestimonials(data || []);
    } catch (error) {
      console.error("Error loading testimonials:", error);
    } finally {
      setLoading(false);
    }
  };
  const filterTestimonials = () => {
    let filtered = [...testimonials];
    if (ratingFilter !== "all") {
      const minRating = parseInt(ratingFilter);
      filtered = filtered.filter(t => t.rating >= minRating);
    }
    if (projectFilter !== "all") {
      filtered = filtered.filter(t => t.project_type === projectFilter);
    }
    setFilteredTestimonials(filtered);
  };
  const projectTypes = Array.from(new Set(testimonials.map(t => t.project_type).filter(Boolean))) as string[];
  const getInitials = (name: string) => {
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };
  return <>
      <SEO title="Client Testimonials | 4FOX Security" description="Read what our clients say about our cyber security services. Trusted by leading organisations for VCISO, risk management, and security assessments." />
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="pt-24 pb-16 bg-secondary/30">
          <div className="container mx-auto px-4">
            {/* Hero Section */}
            <div className="text-center mb-16 bg-muted/30 rounded-lg py-12 px-4">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                What Our Clients Say
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">Trusted by leading organisations to strengthen their cyber security posture</p>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-4 items-center justify-center">
                <div className="w-48">
                  <Select value={ratingFilter} onValueChange={setRatingFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ratings</SelectItem>
                      <SelectItem value="5">5 Stars</SelectItem>
                      <SelectItem value="4">4+ Stars</SelectItem>
                      <SelectItem value="3">3+ Stars</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {projectTypes.length > 0 && <div className="w-64">
                    <Select value={projectFilter} onValueChange={setProjectFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Filter by project" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Projects</SelectItem>
                        {projectTypes.map(type => <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>}
              </div>
            </div>

            {/* Testimonials Grid */}
            {loading ? <div className="text-center py-12">
                <p className="text-muted-foreground">Loading testimonials...</p>
              </div> : filteredTestimonials.length === 0 ? <div className="text-center py-12">
                <p className="text-muted-foreground">No testimonials found matching your filters.</p>
              </div> : <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredTestimonials.map(testimonial => {
              return <Card key={testimonial.id} className={`group transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-accent hover:border-2 h-full ${testimonial.featured ? "bg-gradient-to-br from-primary/5 to-accent/5 border-2 border-primary" : ""}`}>
                      <CardHeader className="pb-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-muted text-muted-foreground group-hover:bg-accent group-hover:text-white transition-colors duration-300">
                            <Quote className="h-6 w-6" />
                          </div>
                          {testimonial.featured && <Badge className="bg-accent text-accent-foreground">Featured</Badge>}
                        </div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex gap-1">
                            {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`h-5 w-5 transition-colors duration-300 ${star <= testimonial.rating ? "fill-muted-foreground/40 text-muted-foreground/40 group-hover:fill-accent group-hover:text-accent" : "text-muted-foreground/30"}`} />)}
                          </div>
                        </div>
                        {testimonial.project_type && <Badge variant="outline" className="w-fit">
                            {testimonial.project_type}
                          </Badge>}
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-muted-foreground mb-6 text-base leading-relaxed">
                          "{testimonial.testimonial_text}"
                        </p>
                        <div className="flex items-start gap-4 pt-4 border-t">
                          <Avatar className="h-12 w-12 ring-2 ring-primary/10">
                            {testimonial.client_photo_url ? <AvatarImage src={testimonial.client_photo_url} alt={testimonial.client_name} /> : <AvatarFallback>
                                {getInitials(testimonial.client_name)}
                              </AvatarFallback>}
                          </Avatar>
                          <div className="flex-grow min-w-0">
                            <p className="font-semibold text-base">{testimonial.client_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {testimonial.client_position}
                            </p>
                            <p className="text-sm text-muted-foreground font-medium">
                              {testimonial.client_company}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>;
            })}
              </div>}
          </div>
        </main>

        <Footer />
      </div>
    </>;
};
export default Testimonials;